import type { OpenOptions, OpenResult, OpenTarget, Unsubscribe } from "../Platform";
import { urlForTarget } from "../targets";
import { browserNavigationTarget, scopedBrowserPath } from "../../utils/tenantRoute";

import { requestLeave } from "../../shell/leaveGuardStore";

/** Navigates the browser's router to a same-origin path (`pathname + search + hash`). */
export type WebNavigateHandler = (path: string) => void;

let currentHandler: WebNavigateHandler | null = null;

/**
 * Installs the router-owned in-app navigate for this document. The web adapter publishes
 * it as `navigation.installNavigateHandler`; `App` calls that from inside `BrowserRouter`,
 * and the adapter's `open`, created before React mounts, routes through it. The mirror
 * of the desktop's `installOpenIntentHandler`.
 */
export function installWebNavigateHandler(handler: WebNavigateHandler): Unsubscribe {
    currentHandler = handler;
    return () => {
        if (currentHandler === handler) currentHandler = null;
    };
}

/**
 * The web platform's `open`. The target's web URL comes from `urlForTarget` (the one
 * place that knows the routes). A new tab or window uses `window.open`. A same-tab
 * open of a same-origin URL goes through the installed router navigate; another
 * origin (an external site, `mailto:`) loads in place. "fallback" only when the
 * target has no web URL or no router has installed its navigate yet.
 *
 * Browser destinations carry the company/project prefix. TenantRouter normalizes
 * that prefix for same-document navigation after React Router applies its basename.
 */
export function openOnWeb(target: OpenTarget, options?: OpenOptions): OpenResult {
    const url = urlForTarget(target);
    if (url === null) return "fallback";
    if (options?.newWindow || options?.newTab) {
        window.open(scopedBrowserPath(url), "_blank", "noopener,noreferrer");
        return "handled";
    }
    const resolved = new URL(url, window.location.origin);
    if (resolved.origin !== window.location.origin) {
        window.location.assign(url);
        return "handled";
    }
    const destination = browserNavigationTarget(url);
    if (destination.routerPath === null) {
        requestLeave(() => {
            // pagehide closes admission only after the browser commits this
            // navigation. Cancelling a native unload prompt keeps this page usable.
            window.location.assign(destination.href);
        });
        return "handled";
    }
    if (currentHandler === null) return "fallback";
    // TenantRouter normalizes an explicit canonical target after Router adds
    // its basename. Keeping the destination explicit also preserves a scoped
    // instance of a global surface (the project-filtered revision dashboard).
    currentHandler(destination.href);
    return "handled";
}
